import { useCart } from '../../contexts/CartContext';
import { Link } from 'react-router-dom';

export default function CartSidebar() {
  const { cartItems, removeFromCart, updateQuantity, cartTotal, isCartOpen, closeCart, cartCount } = useCart();

  if (!isCartOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={closeCart}
      ></div>

      {/* Sidebar */}
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-[#0A0A0A] border-l border-white/10 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h2 className="text-2xl font-black text-white">YOUR BAG</h2>
            <p className="text-sm text-gray-400 mt-1">
              {cartCount} {cartCount === 1 ? 'item' : 'items'}
            </p>
          </div>
          <button
            onClick={closeCart}
            className="w-10 h-10 flex items-center justify-center text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <div className="w-20 h-20 mb-6 flex items-center justify-center bg-white/5 rounded-full">
                <i className="ri-shopping-bag-3-line text-4xl text-gray-600"></i>
              </div>
              <h3 className="text-xl font-black text-white mb-2">Your bag is empty</h3>
              <p className="text-gray-400 mb-6">Add some items to get started</p>
              <button
                onClick={closeCart}
                className="px-8 py-3 bg-white text-black rounded-lg font-bold text-sm hover:scale-105 transition-all whitespace-nowrap"
              >
                CONTINUE SHOPPING
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item, index) => (
                <div
                  key={`${item.id}-${item.size}-${item.color}-${index}`}
                  className="flex gap-4 p-4 bg-[#1A1A1A] border border-white/10 rounded-lg"
                >
                  {/* Product Image */}
                  <Link
                    to={`/product/${item.id}`}
                    onClick={closeCart}
                    className="flex-shrink-0 w-24 h-24 bg-black rounded-lg overflow-hidden"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </Link>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <Link
                        to={`/product/${item.id}`}
                        onClick={closeCart}
                        className="flex-1"
                      >
                        <p className="text-xs font-bold tracking-[0.15em] uppercase text-gray-500 mb-1">
                          {item.category}
                        </p>
                        <h4 className="text-sm font-bold text-white line-clamp-2 hover:text-gray-300 transition-colors">
                          {item.name}
                        </h4>
                      </Link>
                      <button
                        onClick={() => removeFromCart(item.id, item.size, item.color)}
                        className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-white transition-colors"
                      >
                        <i className="ri-close-line text-lg"></i>
                      </button>
                    </div>

                    {/* Size & Color */}
                    <div className="flex items-center gap-3 mb-3">
                      {item.size && (
                        <span className="text-xs font-bold text-gray-400">
                          Size: <span className="text-white">{item.size}</span>
                        </span>
                      )}
                      {item.color && (
                        <span className="text-xs font-bold text-gray-400">
                          Color: <span className="text-white">{item.color}</span>
                        </span>
                      )}
                    </div>

                    {/* Price & Quantity */}
                    <div className="flex items-center justify-between">
                      <p className="text-lg font-black text-white">${item.price}</p>

                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2 bg-black border border-white/20 rounded-lg">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1, item.size, item.color)}
                          className="w-8 h-8 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
                        >
                          <i className="ri-subtract-line"></i>
                        </button>
                        <span className="text-sm font-bold text-white w-8 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1, item.size, item.color)}
                          className="w-8 h-8 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
                        >
                          <i className="ri-add-line"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="border-t border-white/10 p-6 space-y-4">
            {/* Subtotal */}
            <div className="flex items-center justify-between text-lg">
              <span className="font-bold text-gray-400">Subtotal</span>
              <span className="font-black text-white text-2xl">${cartTotal.toFixed(2)}</span>
            </div>

            {/* Shipping Note */}
            <p className="text-xs text-gray-500 text-center">
              Shipping and taxes calculated at checkout
            </p>

            {/* Checkout Button */}
            <button className="w-full bg-white text-black py-4 rounded-lg font-black text-sm tracking-wide hover:scale-105 transition-all duration-300 whitespace-nowrap">
              PROCEED TO CHECKOUT
            </button>

            {/* Continue Shopping */}
            <button
              onClick={closeCart}
              className="w-full border border-white/20 text-white py-4 rounded-lg font-bold text-sm tracking-wide hover:bg-white/5 transition-all whitespace-nowrap"
            >
              CONTINUE SHOPPING
            </button>
          </div>
        )}
      </div>
    </div>
  );
}